import React, { useEffect, useReducer, useRef } from 'react'
import { GridSortModel } from '@mui/x-data-grid'
import snakeCase from 'lodash/snakeCase'

import { createSegment } from 'api/Participant/Segment/Action'
import ParticipantsComp from 'components/Participants'
import { fetchParticipants } from 'api/Participant/Action'
import Query from 'redux/PrepareQuery'
import participantsReducer from 'containers/Participants/Redux/Reducer'
import { initialState as state } from 'containers/Participants/Redux/Reducer/InitialState'
import { resetParticipantContext } from 'containers/Participants/Redux/Actions/FilterBuilder'
import {
  setParticipants,
  setSort,
  searchByNameOrEmail,
  updateSelection,
  setGridLoaderStatus,
} from 'containers/Participants/Redux/Actions/ParticipantGrid'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import { createFiltersArray } from 'lib/participants/Helper'
import { fetchHubs } from 'redux/thunks/Hubs'
import { fetchProjects } from 'redux/thunks/Projects'
import { fetchParticipantGroups } from 'redux/thunks/ParticipantGroups'
import { fetchProjectTags } from 'redux/thunks/ProjectTags'
import { fetchParticipantTags } from 'redux/thunks/ParticipantTags'
import { fetchLocations } from 'redux/thunks/Locations'
import { setSelectAll } from 'containers/Participants/Redux/Actions/SelectAll'
import { SelectAll } from 'types/SelectAll'
import { setSegmentId } from 'redux/slices/FilterBuilder'
import { hubsParam, projectsParam } from 'containers/Participants/ApiParams'
import { getRequestParams } from 'components/Participants/Filters/SignUpQuestions/Helper'
import { ascGroupsParam } from 'api/ApiActions/Groups/FilterByGroup'

export const ParticpantsContext = React.createContext<any>({
  state: state,
  functions: {
    onSearch: () => null,
    onSortModelChange: () => null,
    onClearFilter: () => null,
    onSelection: () => null,
  },
})

const Participants = () => {
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [componentState, dispatch] = useReducer(participantsReducer, state)
  const user = useAppSelector((state: { user: any }) => state.user.user)
  const { selectedFilters, segmentId } = useAppSelector(
    (state) => state.filterBuilder,
  )
  const selectedQuestions = useAppSelector(
    (state) => state.signUpQuestions.selectedQuestions,
  )

  const hasFilterValueChanged = useAppSelector(
    (state) => state.filterBuilder.hasFilterValueChanged,
  )

  const globalDispatch = useAppDispatch()

  const fetchPageAPI = async () => {
    dispatch(setGridLoaderStatus(true))

    const query = new Query(
      { ...componentState.queries, participant_segment_id: segmentId },
      componentState.filterQuery,
    )

    const response = await fetchParticipants(query)
    const { participants: listOfParticipants = [], meta = {} } = response
    if (response && !response.code && response.code !== 'ERR_CANCELED') {
      dispatch(setParticipants(listOfParticipants, meta))
    }
  }

  const prepareFilters = async () => {
    let filtersArray = createFiltersArray(selectedFilters)

    // Sign up form filter params
    const signupFormSelectedQuestions = getRequestParams(selectedQuestions)

    filtersArray = [...filtersArray, ...signupFormSelectedQuestions]
    if (filtersArray.length !== 0) {
      const segmentId = await createSegment(user.id, filtersArray)
      globalDispatch(setSegmentId(segmentId))

      dispatch(
        setSort({ login: 'desc' }, [
          {
            field: 'login',
            sort: 'desc',
          },
        ]),
      )
    } else {
      /**
       * ⚠️ Reset previous segment ID then.
       */
      globalDispatch(setSegmentId(0))
    }
  }

  const onSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      const fieldName = snakeCase(model[0].field)
      const updatedSort = {
        [fieldName]: model[0].sort,
      }
      dispatch(setSort(updatedSort, model))
    }
  }

  const onSearch = () => {
    dispatch(searchByNameOrEmail(searchInputRef?.current?.value || ''))
  }

  const onClearFilter = () => {
    dispatch(resetParticipantContext())
  }

  const onSelection = (participantIDs: string[]) => {
    dispatch(updateSelection(participantIDs))
  }

  const setSelectAllObj = (selectionObj: SelectAll) => {
    dispatch(setSelectAll(selectionObj))
  }

  useEffect(() => {
    globalDispatch(fetchHubs(hubsParam))
    globalDispatch(fetchProjects(projectsParam))
    globalDispatch(fetchProjectTags())
    globalDispatch(fetchParticipantTags())
    globalDispatch(fetchLocations())
    globalDispatch(fetchParticipantGroups(ascGroupsParam))
  }, [])

  useEffect(() => {
    fetchPageAPI()
  }, [componentState.queries, componentState.filterQuery, segmentId])

  useEffect(() => {
    if (hasFilterValueChanged) {
      prepareFilters()
    }
  }, [selectedFilters, selectedQuestions])

  useEffect(() => {
    setSelectAllObj({
      all: false,
      allVisible: false,
      page: 0,
    })

    onSelection([])
  }, [selectedFilters])

  const Component = (
    <ParticpantsContext.Provider
      value={{
        state: {
          ...componentState,
          ...{
            searchInputRef: searchInputRef,
            clearable: searchInputRef.current?.value ? true : false,
          },
        },
        functions: {
          onSearch,
          onSortModelChange,
          onClearFilter,
          dispatch,
          onSelection,
          setSelectAllObj,
        },
      }}
    >
      <ParticipantsComp dispatch={dispatch} state={componentState} />
    </ParticpantsContext.Provider>
  )

  return Component
}

export default Participants
